import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TableRow, TableCell } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { setCardsParamsNoFetch, resetAll } from '@app/store/slices/cardsSlice';
import { setMyTeamParams } from '@app/store/slices/myTeamSlice';
import { ThemeButton } from '@shared/ui/buttons';
import { CounterIcon, Status } from '@shared/ui/parts';
import { useViewChartDialog } from '@utils/dialogs-hooks';
import { getTotalCards } from '@utils/helpers';
import { theme } from '@utils/libs/theme';
import { teamsColumns } from '@utils/tables/columns';
import { StatusCreatedAtCell } from '@widgets/tables/cells/common';
import {
  TeamActionCell,
  TeamAvatar,
  TeamCell,
  TeamName,
} from '@widgets/tables/cells/teams';
import { CustomTable, CustomTableMobile } from '@widgets/tables/main';

export default function TeamsTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    groups,
    params,
    count_total,
    count_filtered,
    isLoading,
    isInitialized,
    search,
  } = useSelector((state) => state.my_team);
  const { openViewChartDialog } = useViewChartDialog();
  const handleSetParams = (newParams) => {
    dispatch(setMyTeamParams(newParams));
  };

  return isMobile ? (
    <CustomTableMobile
      params={params}
      setParams={handleSetParams}
      totalCount={count_total || 0}
      filteredCount={count_filtered || 0}
      isLoading={isLoading}
      isInitialized={isInitialized}
      search={search}
      data={groups}
    >
      {groups?.map((row) => (
        <Link
          to={`/teams/${row.slug}`}
          key={row.id}
          className="tableMobile__list-item teamsMobile__item"
        >
          <TeamAvatar team={row} />
          <div className="teamsMobile__item-details">
            <TeamName team={row} />
            <div className="teamsMobile__item-counters">
              <CounterIcon icon="user-input" value={row.users?.length} />
              <CounterIcon icon="card-input" value={getTotalCards(row)} />
            </div>
          </div>
          <div className="teamsMobile__item-status">
            <Status data={row} type="team" />
          </div>
        </Link>
      ))}
    </CustomTableMobile>
  ) : (
    <CustomTable
      columns={teamsColumns}
      data={groups}
      filteredCount={count_filtered || 0}
      totalCount={count_total || 0}
      params={params}
      setParams={handleSetParams}
      isLoading={isLoading}
      isInitialized={isInitialized}
      search={search}
    >
      {groups?.map((row) => (
        <TableRow
          onClick={(e) => {
            if (e.target.closest('[data-skip-row-click]')) return;
            navigate(`/teams/${row.slug}`);
          }}
          tabIndex={-1}
          key={row.id}
        >
          <TeamCell row={row} />
          <StatusCreatedAtCell row={row} type="team" />
          <TableCell align="right">{row.users?.length ?? 0}</TableCell>
          <TableCell align="right">
            <button
              className="dashed-btn inline-flex"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(resetAll());
                dispatch(setCardsParamsNoFetch({ page: 1, group_id: row.id }));
                navigate('/cards');
              }}
            >
              {getTotalCards(row)}
            </button>
          </TableCell>
          <TableCell data-skip-row-click>
            <ThemeButton
              startIcon="chart"
              block={false}
              appearance="secondary"
              className="m-l-auto"
              onClick={() => openViewChartDialog(row)}
            >
              {t('view_chart')}
            </ThemeButton>
          </TableCell>
          <TeamActionCell row={row} />
        </TableRow>
      ))}
    </CustomTable>
  );
}
